/* eslint-disable react/prop-types */
/**
 * BulkConfigIO — reusable "Download .xlsx / Import .xlsx" control with a preview→confirm dialog.
 * Works against any backend that follows the bulk-config contract:
 *   GET  exportUrl                    → streams the .xlsx
 *   POST previewUrl (multipart file)  → { data: { items, summary } }  (no writes)
 *   POST applyUrl   (multipart file)  → { data: { added, modified, deleted, backup, errors } }
 * where summary = { add, modify, delete, unchanged, error, skip, total } and each item =
 *   { row, action: 'add'|'modify'|'delete'|'unchanged'|'error', name, errors[], warnings[] }.
 */
import { useCallback, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useAuthedFileApi } from "hooks/useAuthedFileApi";

const BRAND = "#52009b";
const ACTION_COLOR = {
  add: "#2e7d32",
  modify: "#0a5bb0",
  delete: "#c62828",
  unchanged: "#7b809a",
  error: "#c62828",
};

function Chip({ label, color, count }) {
  if (!count) return null;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: "0.72rem",
        fontWeight: 700,
        color: "#fff",
        background: color,
        borderRadius: 6,
        padding: "2px 9px",
      }}
    >
      {count} {label}
    </span>
  );
}

export default function BulkConfigIO({
  title,
  exportUrl,
  previewUrl,
  applyUrl,
  filename = "config.xlsx",
  onApplied,
  helpText,
}) {
  const { download, upload } = useAuthedFileApi();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [diff, setDiff] = useState(null);
  const [file, setFile] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [open, setOpen] = useState(false);

  const doExport = useCallback(async () => {
    setErrMsg("");
    setBusy("export");
    try {
      await download(exportUrl, filename);
    } catch (e) {
      setErrMsg(e?.message || "Download failed.");
    } finally {
      setBusy("");
    }
  }, [download, exportUrl, filename]);

  const onPick = useCallback(
    async (e) => {
      const picked = e.target.files?.[0];
      e.target.value = ""; // allow re-selecting the same file
      if (!picked) return;
      setFile(picked);
      setDiff(null);
      setApplyResult(null);
      setErrMsg("");
      setOpen(true);
      setBusy("preview");
      try {
        const res = await upload(previewUrl, picked);
        if (res?.data?.summary) setDiff(res.data);
        else setErrMsg(res?.message || "Could not read the file.");
      } catch (err) {
        setErrMsg(err?.message || "Could not read the file.");
      } finally {
        setBusy("");
      }
    },
    [upload, previewUrl]
  );

  const doApply = useCallback(async () => {
    if (!file) return;
    setBusy("apply");
    setErrMsg("");
    try {
      const res = await upload(applyUrl, file);
      if (res?.ok && res?.data) {
        setApplyResult(res.data);
        if (onApplied) onApplied();
      } else {
        // Backend refused (e.g. errors slipped in) — surface message + refreshed diff if present.
        setErrMsg(res?.message || "Apply failed.");
        if (res?.data?.summary) setDiff(res.data);
      }
    } catch (err) {
      setErrMsg(err?.message || "Apply failed.");
    } finally {
      setBusy("");
    }
  }, [file, upload, applyUrl, onApplied]);

  const close = useCallback(() => {
    setOpen(false);
    setDiff(null);
    setFile(null);
    setApplyResult(null);
    setErrMsg("");
  }, []);

  const summary = diff?.summary;
  const changeCount = summary ? summary.add + summary.modify + summary.delete : 0;
  const canApply = summary && summary.error === 0 && changeCount > 0 && !applyResult;
  const shownItems = (diff?.items || []).filter(
    (it) => it.action !== "unchanged" && it.action !== "skip"
  );

  return (
    <MDBox sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      <MDButton
        size="small"
        variant="outlined"
        onClick={doExport}
        disabled={busy === "export"}
        startIcon={<Icon>download</Icon>}
        sx={{ color: BRAND, borderColor: BRAND, textTransform: "none" }}
      >
        {busy === "export" ? "Preparing…" : "Download .xlsx"}
      </MDButton>
      <MDButton
        size="small"
        variant="outlined"
        onClick={() => inputRef.current?.click()}
        startIcon={<Icon>upload</Icon>}
        sx={{ color: BRAND, borderColor: BRAND, textTransform: "none" }}
      >
        Import .xlsx
      </MDButton>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: "none" }}
        onChange={onPick}
      />
      {errMsg && !open && (
        <MDTypography variant="caption" sx={{ color: "#c62828" }}>
          {errMsg}
        </MDTypography>
      )}

      <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: BRAND }}>
          {title ? `${title} — import preview` : "Import preview"}
        </DialogTitle>
        <DialogContent dividers>
          {busy === "preview" && (
            <MDBox sx={{ py: 4, textAlign: "center" }}>
              <CircularProgress size={22} sx={{ color: BRAND }} />
              <MDTypography variant="body2" sx={{ color: "#7b809a", mt: 1 }}>
                Reading {file?.name}…
              </MDTypography>
            </MDBox>
          )}

          {errMsg && (
            <MDBox sx={{ mb: 2, p: 1.5, borderRadius: 1, background: "#fdecea", color: "#c62828" }}>
              <MDTypography variant="body2" sx={{ color: "#c62828" }}>
                {errMsg}
              </MDTypography>
            </MDBox>
          )}

          {applyResult && (
            <MDBox sx={{ mb: 2, p: 1.5, borderRadius: 1, background: "#e8f5e9" }}>
              <MDTypography variant="body2" sx={{ color: "#2e7d32", fontWeight: 700 }}>
                Saved — {applyResult.added} added, {applyResult.modified} modified,{" "}
                {applyResult.deleted} deleted.
              </MDTypography>
              {applyResult.backup && (
                <MDTypography variant="caption" sx={{ color: "#7b809a" }}>
                  A backup of the previous state was saved as {applyResult.backup}.
                </MDTypography>
              )}
              {(applyResult.errors || []).length > 0 && (
                <MDTypography variant="caption" sx={{ color: "#c62828", display: "block" }}>
                  {applyResult.errors.length} row(s) failed to write — see logs.
                </MDTypography>
              )}
            </MDBox>
          )}

          {summary && !applyResult && (
            <>
              <MDBox sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                <Chip label="to add" color={ACTION_COLOR.add} count={summary.add} />
                <Chip label="to change" color={ACTION_COLOR.modify} count={summary.modify} />
                <Chip label="to delete" color={ACTION_COLOR.delete} count={summary.delete} />
                <Chip label="unchanged" color={ACTION_COLOR.unchanged} count={summary.unchanged} />
                <Chip label="error(s)" color={ACTION_COLOR.error} count={summary.error} />
              </MDBox>
              {summary.error > 0 && (
                <MDTypography variant="caption" sx={{ color: "#c62828", display: "block", mb: 1 }}>
                  Fix the errors below and re-import — nothing is saved while there are errors.
                </MDTypography>
              )}
              {changeCount === 0 && summary.error === 0 && (
                <MDTypography variant="body2" sx={{ color: "#7b809a", mb: 1 }}>
                  No changes detected in this file.
                </MDTypography>
              )}
              <MDBox sx={{ maxHeight: 340, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "#7b809a" }}>
                      <th style={{ padding: "4px 8px" }}>Row</th>
                      <th style={{ padding: "4px 8px" }}>Action</th>
                      <th style={{ padding: "4px 8px" }}>Name</th>
                      <th style={{ padding: "4px 8px" }}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownItems.map((it) => (
                      <tr key={it.row} style={{ borderTop: "1px solid #eee" }}>
                        <td style={{ padding: "4px 8px", color: "#7b809a" }}>{it.row}</td>
                        <td
                          style={{
                            padding: "4px 8px",
                            fontWeight: 700,
                            color: ACTION_COLOR[it.action] || "#344767",
                            textTransform: "capitalize",
                          }}
                        >
                          {it.action}
                        </td>
                        <td style={{ padding: "4px 8px", color: "#344767" }}>{it.name || "—"}</td>
                        <td style={{ padding: "4px 8px" }}>
                          {(it.errors || []).map((m) => (
                            <div key={m} style={{ color: "#c62828" }}>
                              {m}
                            </div>
                          ))}
                          {(it.warnings || []).map((m) => (
                            <div key={m} style={{ color: "#8a6300" }}>
                              {m}
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </MDBox>
              {helpText && (
                <MDTypography
                  variant="caption"
                  sx={{ color: "#7b809a", display: "block", mt: 1.5 }}
                >
                  {helpText}
                </MDTypography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={close} variant="text" sx={{ textTransform: "none", color: "#7b809a" }}>
            {applyResult ? "Close" : "Cancel"}
          </MDButton>
          {!applyResult && (
            <MDButton
              onClick={doApply}
              variant="contained"
              disabled={!canApply || busy === "apply"}
              sx={{
                bgcolor: BRAND,
                color: "#fff",
                textTransform: "none",
                "&:hover": { bgcolor: "#3a0070" },
              }}
            >
              {busy === "apply"
                ? "Saving…"
                : `Apply ${changeCount} change${changeCount === 1 ? "" : "s"}`}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
